import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Tooltip, Modal, Divider, Flex, Popconfirm, Spin } from "antd";
import { useTableSearch } from "../../../functions/tableSearch";
import EmissionSourceForm from "./EmissionSourceForm";
import { useEffect, useState, useContext } from "react";
import { IndexContext } from "../../../context/IndexContext";
import useFetch from "../../../hooks/useFetch";
import DataTable from "../../common/DataTable";

export default function EmissionSourceView() {
  const { getColumnSearchProps } = useTableSearch();

  const [fetchWrapper] = useFetch();
  const [emissionSourceDetails, setEmissionSourceDetails] = useState([]);

  const [emissionSourceTableLoading, setEmissionSourceTableLoading] =
    useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [formInitialValues, setFormInitialValues] = useState({});

  // load index context
  const { messageApi } = useContext(IndexContext);

  const emissionSourceTableColumns = [
    {
      title: "Scope",
      dataIndex: "ScopeDisplayName",
      key: "ScopeDisplayName",
      width: "10%",
      filters: (emissionSourceDetails ?? []).map((item) => ({
        text: item?.ScopeDisplayName,
        value: item?.ScopeDisplayName,
      })),
      filterSearch: true,
      onFilter: (value, record) => record.ScopeDisplayName.startsWith(value),
    },
    {
      title: "Scope Type",
      dataIndex: "ScopeTypeDisplayName",
      key: "ScopeTypeDisplayName",
      width: "16%",
      filters: (emissionSourceDetails ?? []).map((item) => ({
        text: item?.ScopeTypeDisplayName,
        value: item?.ScopeTypeDisplayName,
      })),
      filterSearch: true,
      onFilter: (value, record) =>
        record.ScopeTypeDisplayName.startsWith(value),
    },
    {
      title: "Category Type",
      dataIndex: "CategoryTypeDisplayName",
      key: "CategoryTypeDisplayName",
      width: "16%",
      filters: (emissionSourceDetails ?? []).map((item) => ({
        text: item?.CategoryTypeDisplayName,
        value: item?.CategoryTypeDisplayName,
      })),
      filterSearch: true,
      onFilter: (value, record) =>
        record.CategoryTypeDisplayName.startsWith(value),
    },
    {
      title: "Emission Source",
      dataIndex: "EmissionSource",
      key: "EmissionSource",
      width: "16%",
      ...getColumnSearchProps("EmissionSource"),
    },
    {
      title: "Dimension",
      dataIndex: "Dimension",
      key: "Dimension",
      width: "16%",
      filters: (emissionSourceDetails ?? []).map((item) => ({
        text: item?.Dimension,
        value: item?.Dimension,
      })),
      filterSearch: true,
      onFilter: (value, record) => record.Dimension.startsWith(value),
    },
    {
      title: "Remark",
      dataIndex: "Remark",
      key: "Remark",
      width: "16%",
      ...getColumnSearchProps("Remark"),
    },
    {
      title: "Action",
      dataIndex: "Action",
      key: "Action",
      width: "10%",
      render: (_, record) => (
        <Flex justify="flex-start" align="center" gap={"5px"} wrap={true}>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "blue" }} />}
              onClick={() => {
                getEmissionSourceFormData(record);
                setIsEditModalOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete Emission Source"
              description={"Are you sure to delete this emission source?"}
              onConfirm={() => {
                deleteEmissionSource(record);
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                icon={<DeleteOutlined style={{ color: "red" }} />}
              />
            </Popconfirm>
          </Tooltip>
        </Flex>
      ),
    },
  ];

  function getEmissionSourceFormData(row_record) {
    fetchWrapper({
      endpoint_url: "emission_source_management/getEmissionSourceFormData",
      body: {
        emission_source_id: row_record.ID,
      },
      onSuccess: (response) => {
        setFormInitialValues(response.data);
      },
    });
  }

  /* API to get emission source details */
  function getEmissionSourceDetails() {
    setEmissionSourceTableLoading(true);
    fetchWrapper({
      endpoint_url: "emission_source_management/getEmissionSourceDetails",
      onSuccess: (response) => {
        setEmissionSourceTableLoading(false);
        setEmissionSourceDetails(response.data);
      },
    });
  }

  function deleteEmissionSource(row_record) {
    setEmissionSourceTableLoading(true);
    fetchWrapper({
      endpoint_url: "emission_source_management/deleteEmissionSource",
      body: {
        emission_source_id: row_record.ID,
      },
      onSuccess: (response) => {
        setEmissionSourceTableLoading(false);
        if (response.data.status === true) {
          messageApi.open({
            type: "success",
            content: response.message,
          });
          getEmissionSourceDetails();
        } else {
          messageApi.open({
            type: "error",
            content: response.message,
          });
        }
      },
    });
  }

  useEffect(() => {
    getEmissionSourceDetails();
  }, []);

  return (
    <>
      <Flex>
        <Button
          icon={<PlusOutlined />}
          className="mb-5"
          onClick={() => setIsAddModalOpen(true)}
        >
          New Emission Source
        </Button>
      </Flex>

      {emissionSourceTableLoading === true ? (
        <Flex justify="center" align="center" style={{ height: "50vh" }}>
          <Spin />
        </Flex>
      ) : (
        <DataTable
          table_column={emissionSourceTableColumns}
          table_data={emissionSourceDetails}
          scroll_width={1500}
        />
      )}

      <Modal
        title="Add Emission Source"
        open={isAddModalOpen}
        footer={null}
        closable={false}
        maskClosable={false}
      >
        <Divider className="mb-4 mt-0"></Divider>
        <EmissionSourceForm
          mode="add"
          setIsEmissionSourceFormModalOpen={setIsAddModalOpen}
          isUserGroupForm={false}
          refreshTable={getEmissionSourceDetails}
        />
      </Modal>

      <Modal
        title="Edit Emission Source"
        open={isEditModalOpen}
        footer={null}
        closable={false}
        maskClosable={false}
      >
        <Divider className="mb-4 mt-0"></Divider>
        <EmissionSourceForm
          mode="edit"
          setIsEmissionSourceFormModalOpen={setIsEditModalOpen}
          isUserGroupForm={false}
          refreshTable={getEmissionSourceDetails}
          initialValues={formInitialValues}
        />
      </Modal>
    </>
  );
}
